import type { WidgetConfig, WidgetChatRequest, WidgetChatResponse, PublicApiError } from './types';
import { createWidgetError, WidgetError } from './errors';
import { TokenManager } from '../security/token-manager';
import { validateOrigin } from '../security/origin-validator';
import { validateChatRequest } from '../security/request-validator';
import { TokenBucketRateLimiter } from '../security/rate-limiter';

function generateCorrelationId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function getRuntimeOrigin(): string {
  if (window.location.origin && window.location.origin !== 'null') {
    return window.location.origin;
  }
  return 'https://localhost';
}

function describeApiTarget(endpoint: string, apiVersion: string) {
  let endpointOrigin = endpoint;

  try {
    endpointOrigin = new URL(endpoint).origin;
  } catch {
    // Keep the raw value when the URL is not parseable.
  }

  return {
    endpoint,
    endpointOrigin,
    apiVersion,
    localTarget:
      endpointOrigin === 'http://localhost' ||
      endpointOrigin === 'http://127.0.0.1' ||
      endpointOrigin.startsWith('http://localhost:') ||
      endpointOrigin.startsWith('http://127.0.0.1:'),
  };
}

function logApiTarget(event: string, endpoint: string, apiVersion: string, details?: Record<string, unknown>): void {
  if (typeof console === 'undefined' || typeof console.info !== 'function') {
    return;
  }

  console.info('[FluxBot Widget] API target', {
    event,
    ...describeApiTarget(endpoint, apiVersion),
    ...(details ?? {}),
  });
}

/** Response from POST /api/v1/widget/session */
export interface WidgetSessionResponse {
  status: 'ok';
  sessionId: string;
  expiresAt: string;
  requestId?: string;
}

export class WidgetClient {
  private readonly config: WidgetConfig;
  private readonly tokenManager: TokenManager;
  private readonly limiter = new TokenBucketRateLimiter(5, 5);
  private readonly timeoutMs = 30000;
  private readonly retryDelaysMs = [250, 750, 2000];

  constructor(config: WidgetConfig) {
    this.config = config;
    this.tokenManager = new TokenManager(config.token);
    logApiTarget('client-initialized', this.config.endpoint, this.config.apiVersion);

    if (!this.tokenManager.validateFormat()) {
      throw createWidgetError('INVALID_TOKEN');
    }
    if (this.tokenManager.isExpired()) {
      throw createWidgetError('TOKEN_EXPIRED');
    }
  }

  /**
   * Create a secure widget session via POST /api/v1/widget/session.
   *
   * The backend validates the token (Bearer auth), confirms the parent domain
   * is in the tenant allowlist, and returns a sessionId + sets a secure cookie
   * (`fluxbot_widget_session`) scoped to `/api/v1/widget`.
   *
   * This call must be made from the HOST PAGE (not the iframe) because the
   * backend checks `requestOrigin === selfOrigin` to ensure only the widget
   * frame can create sessions.
   *
   * @returns The session info including sessionId and expiry.
   * @throws WidgetError on auth, domain, or network failures.
   */
  async createSession(parentUrl?: string): Promise<WidgetSessionResponse> {
    if (!this.limiter.consume()) {
      throw createWidgetError('LOCAL_RATE_LIMITED');
    }

    const sessionId = this.generateSessionId();

    logApiTarget('session-create', this.config.endpoint, this.config.apiVersion, {
      parentOrigin: window.location.origin,
    });

    const response = await this.fetchWithTimeout(
      `${this.config.endpoint}/api/${this.config.apiVersion}/widget/session`,
      {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify({
          token: this.tokenManager.getToken(),
          parentOrigin: window.location.origin,
          parentUrl: parentUrl || window.location.href,
          locale: this.config.locale,
          sessionId,
        }),
      },
    );

    if (response.status === 201 || response.status === 200) {
      const data = (await response.json()) as WidgetSessionResponse;
      logApiTarget('session-created', this.config.endpoint, this.config.apiVersion, {
        sessionId: data.sessionId,
        expiresAt: data.expiresAt,
      });
      return data;
    }

    const apiError = await this.parseApiError(response);
    throw createWidgetError(apiError.errorCode, apiError.message);
  }

  async sendMessage(req: WidgetChatRequest): Promise<WidgetChatResponse> {
    if (!this.limiter.consume()) {
      throw createWidgetError('LOCAL_RATE_LIMITED');
    }
    const payload = validateChatRequest(req);
    logApiTarget('chat-request', this.config.endpoint, this.config.apiVersion, {
      messageLength: payload.message.length,
      sessionId: payload.sessionId,
    });
    return this.executeWithRetry(() => this.sendChatRequest(payload));
  }

  async testConnection(): Promise<boolean> {
    if (!this.limiter.consume()) {
      return false;
    }
    try {
      logApiTarget('connection-test', this.config.endpoint, this.config.apiVersion);
      const response = await this.fetchWithTimeout(
        `${this.config.endpoint}/api/${this.config.apiVersion}/widget/connection-test`,
        {
          method: 'POST',
          headers: this.getHeaders(),
          body: JSON.stringify({
            domain: window.location.hostname,
            apiVersion: this.config.apiVersion,
          }),
        },
      );
      return response.status === 200;
    } catch {
      return false;
    }
  }

  /** Get the token for embedding in the iframe URL (legacy flow). */
  getToken(): string {
    return this.tokenManager.getToken();
  }

  private generateSessionId(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    return `sess-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  }

  private async sendChatRequest(req: WidgetChatRequest): Promise<WidgetChatResponse> {
    const response = await this.fetchWithTimeout(
      `${this.config.endpoint}/api/${this.config.apiVersion}/widget/chat`,
      {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify(req),
      },
    );

    if (response.status === 200) {
      return (await response.json()) as WidgetChatResponse;
    }

    const apiError = await this.parseApiError(response);
    throw createWidgetError(apiError.errorCode, apiError.message);
  }

  private async executeWithRetry<T>(fn: () => Promise<T>): Promise<T> {
    let lastError: unknown;

    for (let attempt = 0; attempt <= this.retryDelaysMs.length; attempt += 1) {
      try {
        return await fn();
      } catch (error) {
        lastError = error;
        if (error instanceof WidgetError) {
          if (
            error.errorCode === 'INVALID_TOKEN' ||
            error.errorCode === 'TOKEN_EXPIRED' ||
            error.errorCode === 'DOMAIN_NOT_ALLOWED' ||
            error.errorCode === 'VALIDATION_ERROR' ||
            error.errorCode === 'INVALID_REQUEST'
          ) {
            throw error;
          }
        }

        if (attempt < this.retryDelaysMs.length) {
          await new Promise((resolve) => setTimeout(resolve, this.retryDelaysMs[attempt]));
        }
      }
    }

    throw (lastError as Error) ?? createWidgetError('INTERNAL_ERROR');
  }

  private async fetchWithTimeout(url: string, options: RequestInit): Promise<Response> {
    const originCheck = validateOrigin(window.location.origin, {
      allowHttpLocalhost: true,
    });
    const runtimeOrigin = getRuntimeOrigin();
    const normalizedCheck = validateOrigin(runtimeOrigin, { allowHttpLocalhost: true });
    if (!originCheck.valid && !normalizedCheck.valid) {
      throw createWidgetError('INSECURE_ORIGIN', originCheck.reason || normalizedCheck.reason);
    }

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), this.timeoutMs);

    try {
      return await fetch(url, { ...options, signal: controller.signal, credentials: 'include' });
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        throw createWidgetError('TIMEOUT_ERROR');
      }
      throw createWidgetError('NETWORK_ERROR');
    } finally {
      clearTimeout(timer);
    }
  }

  private async parseApiError(response: Response): Promise<PublicApiError> {
    try {
      const parsed = (await response.json()) as Partial<PublicApiError>;
      if (typeof parsed.errorCode === 'string' && typeof parsed.message === 'string') {
        return { errorCode: parsed.errorCode, message: parsed.message, requestId: parsed.requestId };
      }
    } catch {
      // fall through
    }

    if (response.status === 401) return { errorCode: 'INVALID_TOKEN', message: 'Unauthorized' };
    if (response.status === 403) return { errorCode: 'DOMAIN_NOT_ALLOWED', message: 'Forbidden' };
    if (response.status === 429) return { errorCode: 'RATE_LIMITED', message: 'Rate limited' };
    if (response.status >= 500) return { errorCode: 'BACKEND_UNAVAILABLE', message: 'Backend unavailable' };
    return { errorCode: 'INTERNAL_ERROR', message: 'Unexpected error' };
  }

  private getHeaders(): Record<string, string> {
    return {
      Authorization: `Bearer ${this.tokenManager.getToken()}`,
      'Content-Type': 'application/json',
      Origin: getRuntimeOrigin(),
      'X-Correlation-Id': generateCorrelationId(),
      'X-Client-Version': this.config.clientVersion,
      'X-Widget-Token': this.tokenManager.getToken(),
    };
  }
}
